
import { useState } from "react";
import { Clock, BookmarkPlus, Share2, Paintbrush, BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface Idea {
  id: string;
  title: string;
  description: string;
  difficulty: "easy" | "medium" | "hard";
  timeRequired: string;
  materials: string[];
  instructions: string[];
  storyline?: string;
  imageUrl?: string;
}

interface IdeaCardProps {
  idea: Idea;
  onSave?: (idea: Idea) => void;
  isDetailed?: boolean;
}

export function IdeaCard({ idea, onSave, isDetailed = false }: IdeaCardProps) {
  const [expanded, setExpanded] = useState(isDetailed);
  
  const difficultyColor = {
    easy: "bg-green-100 text-green-800",
    medium: "bg-yellow-100 text-yellow-800",
    hard: "bg-red-100 text-red-800"
  };
  
  return (
    <div 
      className={cn(
        "glass-card rounded-2xl overflow-hidden transition-all duration-300 ease-in-out animate-fade-in-up",
        expanded ? "p-5" : "p-4"
      )}
    >
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex gap-2 mb-2">
              <span className={cn("text-xs font-medium px-2 py-0.5 rounded-full", difficultyColor[idea.difficulty])}>
                {idea.difficulty.charAt(0).toUpperCase() + idea.difficulty.slice(1)}
              </span>
              
              <span className="text-xs bg-blue-100 text-blue-800 font-medium px-2 py-0.5 rounded-full flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {idea.timeRequired}
              </span>
            </div>
            
            <h3 className="font-display font-semibold text-lg">{idea.title}</h3>
          </div>
          
          <div className="flex gap-1">
            {onSave && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => onSave(idea)}
              >
                <BookmarkPlus className="h-4 w-4" />
                <span className="sr-only">Save</span>
              </Button>
            )}
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
            >
              <Share2 className="h-4 w-4" />
              <span className="sr-only">Share</span>
            </Button>
          </div>
        </div>
        
        <p className="text-muted-foreground text-sm">
          {idea.description}
        </p>
        
        {expanded && (
          <div className="space-y-4 mt-2 animate-fade-in">
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                <Paintbrush className="h-4 w-4" />
                Materials Needed
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {idea.materials.map((material, i) => (
                  <li key={i} className="text-sm text-muted-foreground flex items-start">
                    <span className="mr-2 text-primary">•</span>
                    {material}
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold mb-2">How To Create</h4>
              <ol className="space-y-2">
                {idea.instructions.map((step, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs">
                      {i + 1}
                    </span>
                    <span className="text-muted-foreground">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
            
            {idea.storyline && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <BookOpen className="h-4 w-4" />
                  Storyline for Kids
                </h4>
                <div className="text-sm text-muted-foreground bg-primary/5 p-3 rounded-lg italic">
                  {idea.storyline}
                </div>
              </div>
            )}
          </div>
        )}
        
        {!isDetailed && (
          <Button 
            variant="ghost" 
            className="text-primary text-sm justify-center mt-1"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Show Less" : "Show More"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default IdeaCard;
